// src/context/CartContext.jsx
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { formatCurrency } from "../utils/format";

const CartContext = createContext();

function reducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const incoming = action.item;
      const existing = state.items.find((x) => x.ID === incoming.ID);
      const avail = Number(incoming.QTY_AVAILABLE ?? Infinity);

      if (existing) {
        const desired = (existing.qty || 1) + (Number(incoming.qty) || 1);
        const next = Math.max(1, Math.min(desired, avail));
        return {
          ...state,
          items: state.items.map((x) =>
            x.ID === incoming.ID ? { ...x, qty: next } : x
          ),
        };
      }

      const initial = Math.max(1, Math.min(Number(incoming.qty) || 1, avail));
      return {
        ...state,
        items: [...state.items, { ...incoming, qty: initial }],
      };
    }

    case "UPDATE": {
      return {
        ...state,
        items: state.items.map((i) => {
          if (i.ID !== action.id) return i;
          const desired = Number(action.qty) || 1;
          const avail = Number(i.QTY_AVAILABLE ?? Infinity);
          const next = Math.max(1, Math.min(desired, avail));
          return { ...i, qty: next };
        }),
      };
    }

    case "REMOVE": {
      return { ...state, items: state.items.filter((i) => i.ID !== action.id) };
    }

    default:
      return state;
  }
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, { items: [] });

  // Stable action wrappers
  const add = useCallback((item) => dispatch({ type: "ADD", item }), []);
  const updateQty = useCallback(
    (id, qty) => dispatch({ type: "UPDATE", id, qty }),
    []
  );
  const remove = useCallback((id) => dispatch({ type: "REMOVE", id }), []);

  // Derived values
  const itemCount = useMemo(
    () => state.items.reduce((sum, it) => sum + (Number(it.qty) || 0), 0),
    [state.items]
  );

  // Robust number parser for prices that might be strings like "$5,987.09"
  const toNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = Number(v.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  // Sum in cents to avoid floating point drift; expose dollars as subtotalNumber
  const subtotalNumber = useMemo(() => {
    const cents = state.items.reduce((sum, it) => {
      const price = toNumber(it.LIST_PRICE);
      const qty = Number(it.qty) || 0;
      return sum + Math.round(price * 100) * qty;
    }, 0);
    return cents / 100;
  }, [state.items]);

  const subtotal = useMemo(
    () => formatCurrency(subtotalNumber),
    [subtotalNumber]
  );

  const value = useMemo(
    () => ({
      items: state.items,
      add,
      updateQty,
      remove,
      subtotal,
      subtotalNumber, // clean numeric value for API calls
      itemCount,
    }),
    [state.items, add, updateQty, remove, subtotal, subtotalNumber, itemCount]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
